// A thesis, published as a Twetch SYSTEM post.
//
// On Twetch a thesis reads as activity ("shared a thesis on $DIESEL"), not a
// personal post — but it is still the author's own words, signed by their
// wallet, and its signed MAP facts carry the alkane smart tag, so Twetch's
// tag feed (this app's thesis tab and chart markers) indexes it as before.
//
// Two steps, one wallet popup:
//   1. `POST /v1/system-posts/thesis` (device-key signed) mints the canonical
//      announcement for this text + asset.
//   2. The wallet signs the announcement's AIP domain, and the ordinary
//      `POST /v1/posts` publishes it with `systemPostId`.
//
// The domain below is a byte-for-byte port of Twetch's
// `web/src/systemPostProtocol.ts` (the server rebuilds the same cells in
// `system_posts::aip_parts`). Every cell is a string, so the domain is valid
// UTF-8 by construction and a wallet's string signer covers the exact bytes.

import type { TwetchSmartTag } from './twetch-api'

const B_PREFIX = '19HxigV4QyBv3tHpQVcUEQyq1pzZVdoAut'
const MAP_PREFIX = '1PuQa7K62MiKCtssSLKy1kh56WWU7MtUR5'

export interface ThesisAnnouncement {
	id: string
	protocolVersion: number
	userId: number
	kind: string
	action: string
	metadata?: Record<string, string>
	content: string
	createdAtMs: number
}

/// The exact text the wallet signs: OP_RETURN marker (0x6a = "j") followed by
/// the B:// cells, the MAP envelope with its fields in sorted key order, and
/// the replay-guard timestamp.
export function thesisAipText(announcement: ThesisAnnouncement, replayTsMs: number): string {
	if (
		announcement.protocolVersion !== 2 ||
		!announcement.action ||
		!Number.isSafeInteger(announcement.createdAtMs) ||
		!Number.isSafeInteger(replayTsMs)
	) {
		throw new Error('Twetch returned a thesis this app cannot sign.')
	}
	const fields: Record<string, string> = {
		action: announcement.action,
		actor_user_id: String(announcement.userId),
		event_ts: String(announcement.createdAtMs),
		system_id: announcement.id,
		system_kind: announcement.kind,
		system_version: '1',
		...(announcement.metadata ?? {})
	}
	const cells = [
		B_PREFIX,
		announcement.content,
		'text/markdown',
		'UTF-8',
		'|',
		MAP_PREFIX,
		'SET',
		'app',
		'twetch',
		'type',
		'system',
		...Object.entries(fields)
			.sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
			.flatMap(([key, value]) => [key, value]),
		'ts',
		String(replayTsMs),
		'|'
	]
	return `j${cells.join('')}`
}

/// What the announcement must say for this app to put a wallet popup in
/// front of the user: their words, about the asset they chose. Anything else
/// means the server and the composer disagree, and signing would be blind.
export function thesisMatches(
	announcement: ThesisAnnouncement,
	uid: number,
	content: string,
	tag: TwetchSmartTag
): boolean {
	return (
		announcement.userId === uid &&
		announcement.content === content.trim() &&
		announcement.kind === `thesis.${tag.namespace}` &&
		announcement.metadata?.tag_id === tag.id &&
		announcement.metadata?.tag_namespace === tag.namespace
	)
}
